import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import i18n from '../../../i18n'

const API_URL = getApiBaseUrl()
const API_VERSION = getApiVersion()

export interface LoginPayload {
  email: string
  password: string
}

export interface LoginResponse {
  access: string
  refresh: string
}

export interface RegisterPayload {
  email: string
  /** Pseudo affiché sur le site (connexion par courriel). */
  username: string
  password: string
  password_confirm: string
  /** Jeton Turnstile si le widget est affiché (config serveur). */
  turnstile_token?: string
}

export interface RegisterResponse {
  detail: string
}

export interface ConfirmEmailResponse {
  detail: string
}

async function parseErrorMessage(response: Response): Promise<string> {
  const data = await response.json().catch(() => ({} as unknown))
  if (data && typeof data === 'object') {
    const anyData = data as Record<string, unknown>
    if (typeof anyData.detail === 'string' && anyData.detail.trim()) return anyData.detail
    for (const v of Object.values(anyData)) {
      if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v.join(', ')
    }
  }
  return i18n.t('auth:authApi.erreur_http', { status: response.status })
}

export async function requestPasswordReset(
  email: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ detail: string }> {
  const url = `${API_URL}/${API_VERSION}/auth/password/reset/`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim() }),
    signal: options.signal,
  })
  const data = (await response.json().catch(() => ({}))) as { detail?: string }
  if (!response.ok) {
    throw new Error(typeof data.detail === 'string' ? data.detail : await parseErrorMessage(response))
  }
  return { detail: data.detail ?? i18n.t('auth:authApi.demande_enregistree') }
}

export async function confirmPasswordReset(payload: {
  uid: string
  token: string
  new_password: string
  new_password_confirm: string
}, options: { signal?: AbortSignal } = {}): Promise<{ detail: string }> {
  const url = `${API_URL}/${API_VERSION}/auth/password/reset/confirm/`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })
  const data = (await response.json().catch(() => ({}))) as { detail?: string; new_password?: string[] }
  if (!response.ok) {
    if (Array.isArray(data.new_password) && data.new_password.length) {
      throw new Error(data.new_password.join(' '))
    }
    throw new Error(typeof data.detail === 'string' ? data.detail : await parseErrorMessage(response))
  }
  return { detail: data.detail ?? i18n.t('auth:authApi.mot_passe_mis_jour') }
}

export async function login(payload: LoginPayload, options: { signal?: AbortSignal } = {}): Promise<LoginResponse> {
  const url = `${API_URL}/${API_VERSION}/auth/login/`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response))
  }
  return (await response.json()) as LoginResponse
}

export async function register(payload: RegisterPayload, options: { signal?: AbortSignal } = {}): Promise<RegisterResponse> {
  const url = `${API_URL}/${API_VERSION}/auth/register/`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response))
  }
  return (await response.json()) as RegisterResponse
}

/** Inscription « courriel d'abord » : réponse toujours identique (aucune énumération). */
export async function signupRequest(email: string, turnstileToken?: string | null): Promise<{ detail: string }> {
  const response = await fetch(`${API_URL}/${API_VERSION}/auth/signup/request/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}) }),
  })
  if (!response.ok) throw new Error(await parseErrorMessage(response))
  return (await response.json()) as { detail: string }
}

/** Le lien d'inscription est-il valable ? → l'adresse prouvée. */
export async function signupCheck(token: string): Promise<{ email: string }> {
  const response = await fetch(`${API_URL}/${API_VERSION}/auth/signup/check/?token=${encodeURIComponent(token)}`)
  if (!response.ok) throw new Error(await parseErrorMessage(response))
  return (await response.json()) as { email: string }
}

/** Crée le compte (actif, adresse prouvée) et renvoie les jetons de connexion. */
export async function signupComplete(payload: {
  token: string
  username: string
  password: string
  password_confirm: string
}): Promise<{ access: string; refresh: string }> {
  const response = await fetch(`${API_URL}/${API_VERSION}/auth/signup/complete/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error(await parseErrorMessage(response))
  return (await response.json()) as { access: string; refresh: string }
}

export async function confirmEmail(uid: string, token: string): Promise<ConfirmEmailResponse> {
  const url = `${API_URL}/${API_VERSION}/auth/confirm-email/?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`
  const response = await fetch(url)
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.detail ?? i18n.t('auth:authApi.lien_invalide_ou_expire'))
  }
  return data as ConfirmEmailResponse
}

